/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Text, Icon, useDisclosure } from '@chakra-ui/react';
import { IoMdRefresh as RefreshIcon } from 'react-icons/io';
import { useEffect, useRef, useState } from 'react';

import { useQuery } from 'hooks/useCustomQuery';
import getCompany from 'API/Company/getCompany';
import getAppAuthById from 'API/AppAuth/getAppAuthById';
import { useSelector } from 'react-redux';
// import getCompanyServices from '../../../../API/getCompanySevices';
import useServiceWizard from 'hooks/useServiceWizard';
import objChangeDetection from 'utils/objChangeDetection';
import { PlatformEnums } from 'utils/platforms';
import styles from '../../../services/new/ServiceWizard/styles';
import ServiceWizardMsAuth from '../../../services/new/ServiceWizardMsAuth';
import ServiceWizardSyncUsers from '../../../services/new/ServiceWizardSyncUsers';
import DashboardServices from './DashboardServices';
import CreateService from '../CreateService';

import { Error as ErrorView } from '@/components/index';
import { nullError } from 'components/error';
import { Button } from 'components/v4';
import { useDashboardMenu } from 'context/DashboardMenuContext';
import { DefinedAttributesProvider } from 'pages/services/serviceprofiles/vendorDefined/DefinedAttributesContext';
import AddVendorDefined from '../components/VendorCard/VendorDefined/AddVendorDefined';
import AddEightByEight from '../add/AddEightByEight';
import LicenceEntitlements from '../add/LicenceEntitlements';
import {
    QUERY_CACHE_TIME,
    REFETCH_INTERVAL,
    MICROSOFT_GRAPH,
} from '../constants';
import { AddIcon } from '@chakra-ui/icons';
import PartnerConfirmationModal from 'pages/dashboards/v5/add/PartnerConfirmationModal';
import SyncRetirementBanner from '../components/SyncRetirementBanner';
import { useDashboardCompanyServices } from 'hooks/useCustomQuery';
import {
    shouldBypassDashboardShapeCache,
    readDashboardShapeCache,
    writeDashboardShapeCache,
} from '../dashboardShapeCache';

const ServiceDashboardPage = () => {
    // Service Wizard Flow
    const serviceWizard = useServiceWizard();
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { darkMode } = useSelector((state) => state.settings);
    const dashboardShapeCacheUser = useSelector(
        (state) => state.login?.email || state.login?.user || '',
    );
    const {
        isOpen: isPartnerModalOpen,
        onOpen: onPartnerModalOpen,
        onClose: onPartnerModalClose,
    } = useDisclosure();

    // Data
    const [services, setServices] = useState(null);
    const [cachedAppAuths, setCachedAppAuths] = useState([]);
    const prevServicesRef = useRef([]);
    const lastServicesSnapshotKeyRef = useRef('');
    const traceSnapshotRef = useRef('');
    const [hasCachedShape, setHasCachedShape] = useState(false);
    const bypassDashboardShapeCache = shouldBypassDashboardShapeCache();
    const { isQueryDisabled, setIsQueryDisabled } = useDashboardMenu();

    const { data: companyData, isLoading: isCompanyLoading } = useQuery({
        queryKey: ['company', currentCompany],
        queryFn: () => getCompany(currentCompany),
        cacheTime: QUERY_CACHE_TIME,
        refetchInterval: false,
        enabled: !isQueryDisabled && !!currentCompany,
        notifyOnChangeProps: ['data', 'isLoading'],
        refetchOnWindowFocus: false,
        retry: false,
    });

    // `['company', currentCompany]` is shared with Searchbar/CompanyStatus/
    // useURLRewrite (useGetCurrentCompany), so this query can resolve from
    // their cache write instead of its own queryFn — sync via effect on
    // `companyData` itself so serviceWizard.company populates either way.
    useEffect(() => {
        if (companyData) {
            serviceWizard.setCompany(companyData);
        }
    }, [companyData]);

    const dashboardServicesQueryKey = [
        'dashboardServices',
        serviceWizard.flow,
        `company: ${currentCompany}`,
    ];

    const {
        data: dashboardData,
        isLoading: isDashboardLoading,
        isFetching: isDashboardFetching,
    } = useDashboardCompanyServices({
        currentCompany,
        flow: serviceWizard.flow,
        queryKey: dashboardServicesQueryKey,
        options: {
            cacheTime: QUERY_CACHE_TIME,
            refetchInterval:
                !isQueryDisabled && !!currentCompany ? REFETCH_INTERVAL : false,
            enabled: !isQueryDisabled && !!currentCompany,
            notifyOnChangeProps: ['data', 'isLoading', 'isFetching'],
            refetchOnWindowFocus: false,
        },
    });
    const isDashboardBusy = isDashboardLoading || isDashboardFetching;

    // Reset 8x8-specific wizard steps when the company changes; the new
    // company may not support 8x8, and profile data is stale.
    useEffect(() => {
        if (
            serviceWizard.flow === 'AddEightByEight' ||
            serviceWizard.flow === 'LicenceEntitlements'
        ) {
            serviceWizard.setFlow('CreateService');
            serviceWizard.setSelectedServiceProfile(null);
            serviceWizard.setEightByEightCompanyDetails(null);
            serviceWizard.setHasCreateServiceError(null);
        }
    }, [currentCompany]);

    useEffect(() => {
        if (!import.meta.env.DEV) return;
        if (!window.location.search.includes('perf=1')) return;

        const snapshot = {
            flow: serviceWizard.flow,
            currentCompany,
            dashboardCacheMode: bypassDashboardShapeCache
                ? 'bypass'
                : 'default',
            isCompanyLoading,
            hasCachedShape,
            dashboardServices: dashboardData?.services?.length || 0,
            dashboardAppAuths: dashboardData?.appAuths?.length || 0,
            renderedServices: services?.length ?? null,
            isDashboardLoading,
            isDashboardFetching,
            isDashboardBusy,
            isQueryDisabled,
        };
        const nextSnapshot = JSON.stringify(snapshot);

        if (traceSnapshotRef.current === nextSnapshot) return;
        traceSnapshotRef.current = nextSnapshot;

        console.info(`[DASH:trace] ServiceDashboardPage ${nextSnapshot}`);
    }, [
        currentCompany,
        bypassDashboardShapeCache,
        dashboardData,
        hasCachedShape,
        isCompanyLoading,
        isDashboardBusy,
        isDashboardFetching,
        isDashboardLoading,
        isQueryDisabled,
        serviceWizard.flow,
        services,
    ]);

    useEffect(() => {
        lastServicesSnapshotKeyRef.current = '';
        prevServicesRef.current = [];
        setHasCachedShape(false);
        setCachedAppAuths([]);

        if (bypassDashboardShapeCache) {
            setServices(null);
            return;
        }

        const cachedShape = readDashboardShapeCache(
            currentCompany,
            dashboardShapeCacheUser,
        );

        if (cachedShape.services.length || cachedShape.appAuths.length) {
            prevServicesRef.current = cachedShape.services;
            setHasCachedShape(true);
            setCachedAppAuths(cachedShape.appAuths);
            setServices(cachedShape.services);
            return;
        }

        lastServicesSnapshotKeyRef.current = '';
        setServices(null);
    }, [
        currentCompany,
        dashboardShapeCacheUser,
        bypassDashboardShapeCache,
        serviceWizard.flow,
    ]);

    useEffect(() => {
        if (!dashboardData && isDashboardBusy) {
            // If we already have a cached shape, keep rendering it until the
            // consolidated payload hydrates.
            setServices((prev) => {
                if (prev?.length) return prev;
                lastServicesSnapshotKeyRef.current = '';
                return null;
            });
            return;
        }

        const next = dashboardData?.services || [];
        const nextAppAuths = dashboardData?.appAuths || [];

        // If the consolidated payload did not change in any way that affects rendering,
        // skip updating state to avoid re-rendering the entire service list.
        const nextServicesKey = next
            .map((s) => {
                const level = s?.statusDisplay?.statusDisplayLevel;
                const detail =
                    s?.statusDisplay?.statusDisplayDetail ||
                    s?.statusDisplay?.statusDisplayText ||
                    '';
                return `${s?.id}:${s?.serviceType}:${s?.status}:${level}:${detail}`;
            })
            .join('|');
        const nextAppAuthsKey = nextAppAuths
            .map((a) => {
                const level = a?.statusDisplay?.statusDisplayLevel;
                const detail =
                    a?.statusDisplay?.statusDisplayDetail ||
                    a?.statusDisplay?.statusDisplayText ||
                    '';
                return `${a?.id}:${a?.appAuthTypeID}:${a?.platformID}:${a?.status}:${level}:${detail}`;
            })
            .join('|');
        const nextSnapshotKey = `${nextServicesKey}::${nextAppAuthsKey}`;
        if (nextSnapshotKey === lastServicesSnapshotKeyRef.current) {
            return;
        }
        lastServicesSnapshotKeyRef.current = nextSnapshotKey;
        setHasCachedShape(false);
        setCachedAppAuths([]);

        if (next.length || nextAppAuths.length) {
            if (!bypassDashboardShapeCache) {
                writeDashboardShapeCache(
                    currentCompany,
                    dashboardShapeCacheUser,
                    dashboardData,
                );
            }
            const result = objChangeDetection(prevServicesRef.current, next);
            prevServicesRef.current = next;
            setServices((prev) => {
                if (
                    Array.isArray(prev) &&
                    prev.length === result.length &&
                    prev.every((item, idx) => item === result[idx])
                ) {
                    return prev;
                }
                return result;
            });
        } else {
            if (!bypassDashboardShapeCache) {
                writeDashboardShapeCache(
                    currentCompany,
                    dashboardShapeCacheUser,
                    {
                        services: [],
                        appAuths: [],
                    },
                );
            }
            prevServicesRef.current = [];
            setServices((prev) =>
                Array.isArray(prev) && !prev.length ? prev : [],
            );
        }
    }, [
        bypassDashboardShapeCache,
        currentCompany,
        dashboardData,
        dashboardShapeCacheUser,
        isDashboardBusy,
    ]);

    // User would need to click on refresh button if idle for more than 2 minutes
    useEffect(() => {
        if (!isQueryDisabled) {
            const timer = setTimeout(() => {
                setIsQueryDisabled(true);
            }, 120000);

            return () => {
                clearTimeout(timer);
            };
        }

        // reset refresh on navigation
        return () => {
            setIsQueryDisabled(false);
        };
    }, [isQueryDisabled]);

    const handleRefresh = () => {
        setIsQueryDisabled(false);
    };

    const handleAddService = () => {
        if (isCompanyLoading) return;
        if (companyData?.partnerID) {
            serviceWizard.setFlow('CreateService');
        } else {
            onPartnerModalOpen();
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onPartnerModalClose();
        serviceWizard.setFlow('CreateService');
    };

    // 2629: the consolidated dashboard payload's appAuths entry for
    // Microsoft Teams does not carry msGraphAppInstanceID/lastAccessSuccess.
    // /appauth/company/{companyID} doesn't return these values either, so
    // once we find the Teams appAuth in the dashboard payload we fetch its
    // full record by ID (/appauth/{appAuthID}) to decide whether Migrate to
    // New Sync applies.
    const msTeamsAppAuth = (dashboardData?.appAuths || []).find(
        (appAuth) =>
            appAuth?.appAuthTypeID === MICROSOFT_GRAPH &&
            appAuth?.platformID === PlatformEnums.TEAMS,
    );

    const { data: msTeamsAppAuthDetail } = useQuery({
        queryKey: ['appAuthById', msTeamsAppAuth?.id],
        queryFn: async () => {
            const res = await getAppAuthById(msTeamsAppAuth.id);
            return res.data;
        },
        cacheTime: QUERY_CACHE_TIME,
        refetchInterval: false,
        enabled: !isQueryDisabled && !!msTeamsAppAuth?.id,
        notifyOnChangeProps: ['data'],
        refetchOnWindowFocus: false,
        retry: false,
    });

    const isMigrateToNewSync =
        !!msTeamsAppAuth &&
        !msTeamsAppAuthDetail?.msGraphAppInstanceID &&
        !!msTeamsAppAuthDetail?.lastAccessSuccess &&
        msTeamsAppAuthDetail?.statusDisplay?.statusDisplayLevel === 0;

    if (!currentCompany) {
        return <ErrorView error={nullError} />;
    }

    const addButton = (
        <Box display="flex" justifyContent="left" marginBottom="20px">
            <Button
                onClick={handleAddService}
                size="lg"
                colorScheme="gray"
                variant="outline"
                borderStyle="dashed"
                borderWidth="2px"
                borderColor="gray.400"
                bg="transparent"
                _hover={{
                    borderColor: 'blue.400',
                    bg: darkMode ? 'blue.900' : 'blue.50',
                }}
                width="100%"
                maxWidth="1064px"
                height="61px"
                borderRadius="md"
                leftIcon={<Icon as={AddIcon} boxSize={5} />}
                data-walkthrough-id="/dashboard-services/card/add-service">
                <Text fontSize="lg" fontWeight="medium">
                    Add New Service
                </Text>
            </Button>
        </Box>
    );

    const titleName = 'Dashboard';

    return (
        <>
            {serviceWizard.flow === 'Start' && (
                <Box sx={styles.container}>
                    <Text
                        sx={{
                            fontSize: '20',
                            fontWeight: '500',
                            marginBottom: '15px',
                        }}>
                        {titleName}
                    </Text>

                    {serviceWizard.flow === 'Start' && addButton}
                    {isMigrateToNewSync && (
                        <SyncRetirementBanner
                            onUpgrade={() => serviceWizard.microsoftAuth()}
                        />
                    )}
                    {/* US 1917: Adding Sevices to Partner Companies */}
                    <PartnerConfirmationModal
                        colorScheme="messenger"
                        darkMode={darkMode}
                        handleSubmit={handleSubmit}
                        isOpen={isPartnerModalOpen}
                        onClose={onPartnerModalClose}
                        partnerName={companyData?.name}
                    />
                    <DashboardServices
                        serviceWizard={serviceWizard}
                        services={services}
                        cachedAppAuths={cachedAppAuths}
                        dashboardData={dashboardData}
                        companyData={companyData}
                        isDashboardLoading={isDashboardBusy}
                        msTeamsAppAuthDetail={msTeamsAppAuthDetail}
                    />

                    {isQueryDisabled && (
                        <Button
                            borderRadius="2xl"
                            onClick={handleRefresh}
                            style={{
                                position: 'fixed',
                                bottom: 50,
                                right: 50,
                                zIndex: 1000,
                            }}
                            leftIcon={
                                <RefreshIcon size={20} className="text-white" />
                            }>
                            <span className="flex items-center text-white">
                                Refresh
                            </span>
                        </Button>
                    )}
                </Box>
            )}

            {serviceWizard.flow === 'CreateService' && (
                <CreateService
                    serviceWizard={serviceWizard}
                    existingServices={dashboardData?.services}
                />
            )}

            {serviceWizard.flow === 'SelectiveSyncUsers' && (
                <ServiceWizardSyncUsers serviceWizard={serviceWizard} />
            )}

            {serviceWizard.flow === 'SelectiveSyncUsersEdit' && (
                <ServiceWizardSyncUsers serviceWizard={serviceWizard} edit />
            )}

            {serviceWizard.flow === 'MsAuth' && (
                <ServiceWizardMsAuth serviceWizard={serviceWizard} />
            )}

            {serviceWizard.flow === 'VendorDefined' && (
                <DefinedAttributesProvider>
                    <AddVendorDefined serviceWizard={serviceWizard} />
                </DefinedAttributesProvider>
            )}

            {serviceWizard.flow === 'AddEightByEight' && (
                <AddEightByEight serviceWizard={serviceWizard} />
            )}

            {serviceWizard.flow === 'LicenceEntitlements' && (
                <LicenceEntitlements serviceWizard={serviceWizard} />
            )}
        </>
    );
};

export default ServiceDashboardPage;
